import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { ItemsTabFields } from '../../../License/types/fieldEnums';
import { QuotaType } from '../../../License/types/enums';

import { type IComparisonFieldProps } from './types';

import { versionComparisonSelector } from '../../../../store/audit/selectors';
import formatNumber from '../../../../helpers/formatNumber';

import styles from './ComparisonField.module.scss';

function AmountComparisonField({
  label,
  fieldKey,
  uomCodeKey,
  uomNameKey,
  differenceKey,
  isOldVersion,
  className,
  values,
  pathPrefix = '/',
}: IComparisonFieldProps) {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const { differenceMap } = useSelector(versionComparisonSelector);

  const path = `${pathPrefix}${fieldKey}`;
  const valueHasChanged = !!differenceMap[path];
  const value = valueHasChanged
    ? (differenceMap[path]?.[differenceKey] as number | string)
    : (values[fieldKey] as number | string);

  const quotaTypePath = `${pathPrefix}${ItemsTabFields.quotaType}`;
  const quotaTypeValueHasChanged = !!differenceMap[quotaTypePath];
  const quotaTypeValue = quotaTypeValueHasChanged
    ? differenceMap[quotaTypePath]?.[differenceKey]
    : (values[ItemsTabFields.quotaType] as string | number);

  const formattedValue = useMemo(() => {
    if (value == null || value === '') {
      return '-';
    }

    return formatNumber({ value, locale: resolvedLanguage }) as string;
  }, [resolvedLanguage, value]);

  let uomValue = null;
  let uomValueHasChanged = false;

  const uomFieldKey = quotaTypeValue ? (quotaTypeValue === QuotaType.UOM ? uomNameKey : uomCodeKey) : null;

  if (uomFieldKey) {
    const uomPath = `${pathPrefix}${uomFieldKey}`;
    uomValueHasChanged = !!differenceMap[uomPath];

    uomValue = uomValueHasChanged ? differenceMap[uomPath]?.[differenceKey] : (values[uomFieldKey] as string);
  }

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.label}>{t(label)}</div>
      <div
        className={cn(styles.valueContainer, {
          [styles.removed as string]: (valueHasChanged || uomValueHasChanged) && isOldVersion,
          [styles.added as string]: (valueHasChanged || uomValueHasChanged) && !isOldVersion,
        })}
      >
        <div>
          {formattedValue}
          {uomValue && ` ${uomValue as string}`}
        </div>
      </div>
    </div>
  );
}

export default AmountComparisonField;
